import type { BusinessReport } from "./businessReports.types";

export type TemplateSortKey = "template" | "category" | "type" | "topics" | "created" | "lastUsed" | "status";

export interface TemplateSort {
  direction: "asc" | "desc";
  key: TemplateSortKey;
}

export type TemplateCategoryFilterValue =
  "" | `category:${string}` | "synthetic:business-reports" | "synthetic:authoring" | "synthetic:uncategorised";

export interface TemplateCategoryFilterOption {
  label: string;
  value: TemplateCategoryFilterValue;
}

export interface TemplateFilter {
  categoryValue: TemplateCategoryFilterValue;
  hideHidden?: boolean;
  query: string;
}

const UNCATEGORISED_LABELS: Record<BusinessReport["type"], string> = {
  business_report: "Business Reports",
  authoring_template: "Authoring",
  snapshot_template: "Uncategorised",
};

export const getUncategorisedLabel = (type: BusinessReport["type"]): string =>
  UNCATEGORISED_LABELS[type] ?? UNCATEGORISED_LABELS.snapshot_template;

export const getTemplateDisplayCategory = (template: BusinessReport): string =>
  template.category?.name ?? getUncategorisedLabel(template.type);

const TYPE_LABELS: Record<BusinessReport["type"], string> = {
  business_report: "Report",
  authoring_template: "Authoring",
  snapshot_template: "Snapshot",
};

export const getTemplateTypeLabel = (template: BusinessReport): string =>
  TYPE_LABELS[template.type] ?? TYPE_LABELS.snapshot_template;

export const getTemplateStatusLabel = (template: BusinessReport): string => (template.is_active ? "Active" : "Draft");

const getTimestamp = (date: string | null): number | null => {
  if (!date) return null;
  const timestamp = Date.parse(date);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const compareStrings = (first: string, second: string) =>
  first.localeCompare(second, undefined, { sensitivity: "base", numeric: true });

const compareNullableNumbers = (first: number | null, second: number | null, direction: TemplateSort["direction"]) => {
  if (first === null && second === null) return 0;
  if (first === null) return 1;
  if (second === null) return -1;
  return direction === "asc" ? first - second : second - first;
};

export const getTemplateCategoryFilterValue = (template: BusinessReport): TemplateCategoryFilterValue => {
  if (template.category?.id) return `category:${template.category.id}`;
  if (template.type === "business_report") return "synthetic:business-reports";
  if (template.type === "authoring_template") return "synthetic:authoring";
  return "synthetic:uncategorised";
};

export const getTemplateCategoryFilterOption = (category: {
  categoryId: string | null;
  label: string;
}): TemplateCategoryFilterOption => {
  if (category.categoryId) {
    return { label: category.label, value: `category:${category.categoryId}` };
  }

  if (category.label === "Business Reports") {
    return { label: "Business Reports (uncategorised reports)", value: "synthetic:business-reports" };
  }
  if (category.label === "Authoring") {
    return { label: "Authoring (uncategorised authoring templates)", value: "synthetic:authoring" };
  }
  return { label: "Uncategorised (snapshot templates)", value: "synthetic:uncategorised" };
};

export const filterTemplateRows = (templates: readonly BusinessReport[], filter: TemplateFilter): BusinessReport[] => {
  const normalizedQuery = filter.query.trim().toLowerCase();

  return templates.filter(template => {
    const category = getTemplateDisplayCategory(template);

    if (filter.hideHidden && template.is_active === false) return false;
    if (filter.categoryValue && getTemplateCategoryFilterValue(template) !== filter.categoryValue) return false;
    if (!normalizedQuery) return true;

    return [template.name, template.description ?? "", category].some(value =>
      value.toLowerCase().includes(normalizedQuery),
    );
  });
};

export const sortTemplateRows = (templates: readonly BusinessReport[], sort: TemplateSort): BusinessReport[] =>
  [...templates].sort((first, second) => {
    let result = 0;

    switch (sort.key) {
      case "category":
        result = compareStrings(getTemplateDisplayCategory(first), getTemplateDisplayCategory(second));
        break;
      case "type":
        result = compareStrings(getTemplateTypeLabel(first), getTemplateTypeLabel(second));
        break;
      case "topics":
        result = first.prompts.length - second.prompts.length;
        break;
      case "created":
        result = compareNullableNumbers(
          getTimestamp(first.created_at),
          getTimestamp(second.created_at),
          sort.direction,
        );
        break;
      case "lastUsed":
        result = compareNullableNumbers(
          getTimestamp(first.last_used_at),
          getTimestamp(second.last_used_at),
          sort.direction,
        );
        break;
      case "status":
        result = compareStrings(getTemplateStatusLabel(first), getTemplateStatusLabel(second));
        break;
      case "template":
      default:
        result = compareStrings(first.name, second.name);
        break;
    }

    if (result === 0) {
      result = compareStrings(first.name, second.name);
    }

    return sort.key === "created" || sort.key === "lastUsed" || sort.direction === "asc" ? result : -result;
  });
